import type { AgentRow, Note, NoteRef, TerminalRow } from '../transport/api'

/**
 * Receivers pointing at sessions that no longer exist.
 *
 * The server detaches receivers when an agent dies through /agent/kill. Sessions
 * that die any OTHER way — a manual `tmux kill-session`, a reboot — leave their
 * receivers sitting in pending forever, pointed at nothing. The owner has
 * already lost a machine's worth of sessions to a reboot, so this is not a
 * hypothetical.
 *
 * The client can close that gap for free, because it already polls the roster.
 * Three rules keep it from doing harm:
 *
 *  1. LOCAL ONLY. /agent/all lists remote AGENTS but not remote agent-less
 *     terminals, so a remote session with no live agent is invisible to us and
 *     would be misread as dead. Only host === null is judged.
 *  2. THREE CONSECUTIVE ABSENCES. Creating an agent with replace/reuse removes
 *     and recreates the session, so it drops out of the roster for a poll or
 *     two. Flagging on the first miss would offer a "clean this up" button for a
 *     live queue at exactly the moment the user is most likely to tap it.
 *  3. NEVER AUTOMATIC. This only ever renders a marker and offers one tap.
 *     Detaching is the server's decision on kill; the client supplies visibility
 *     for the paths the server cannot see, not a second authority.
 */

/** Absence counts, keyed by session name. Module scope: it must outlive renders. */
const misses = new Map<string, number>()

export const GHOST_AFTER = 3

/** Feed the current roster in on every poll; returns the sessions now considered
 *  gone. Call once per roster update, not per render. */
export function trackLiveSessions(agents: AgentRow[], terminals: TerminalRow[]): Set<string> {
  const live = new Set<string>()
  for (const a of agents) if ((a.host ?? null) === null) live.add(a.session)
  for (const t of terminals) if ((t.host ?? null) === null) live.add(t.session)

  for (const name of live) misses.delete(name)
  for (const name of [...misses.keys()]) if (live.has(name)) misses.delete(name)

  return live
}

/** Record one poll's absences for every session a note still points at. */
export function noteAbsences(notes: Note[], live: Set<string>): Set<string> {
  const referenced = new Set<string>()
  for (const n of notes) {
    for (const r of n.receivers ?? []) {
      if ((r.host ?? null) !== null) continue // rule 1
      if (r.detached_ts) continue // already accounted for
      referenced.add(r.session)
    }
  }
  const ghosts = new Set<string>()
  for (const name of referenced) {
    if (live.has(name)) {
      misses.delete(name)
      continue
    }
    const n = (misses.get(name) ?? 0) + 1
    misses.set(name, n)
    if (n >= GHOST_AFTER) ghosts.add(name)
  }
  return ghosts
}

/** The receivers of one note that are currently considered ghosts. */
export function ghostReceivers(note: Note, ghosts: Set<string>): NoteRef[] {
  return (note.receivers ?? [])
    .filter((r) => (r.host ?? null) === null && !r.detached_ts && ghosts.has(r.session))
    .map((r) => ({ host: null, session: r.session }))
}

/** Test seam: absence counts are module state by design (they must survive
 *  renders), so a test needs a way to start clean. */
export function resetGhostTracking(): void {
  misses.clear()
}
